import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Card, Stack, TextField, Button,
  IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Divider, List, ListItemButton, ListItemText,
  ListItemIcon, Paper, InputAdornment, Tooltip
} from '@mui/material';
import { 
  DataGrid, GridColDef, GridRenderCellParams 
} from '@mui/x-data-grid';

// MUI Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import SearchIcon from '@mui/icons-material/Search';
import CategoryIcon from '@mui/icons-material/Category';
import PostAddIcon from '@mui/icons-material/PostAdd'; // 템플릿 추가 아이콘
import { HttpAdminApi } from '../../../interface/admin-api';
import { userState } from '../../../interface/MainInterface';

interface PropsType {
  userState: userState;
}

interface FAQItem {
  pk: string;
  sk: string;
  faq_id?: string;
  question: string;
  answer: string;
  is_common?: boolean; // 서버에서 내려주는 공통 여부 플래그
}

interface TemplateType {
  pk: string;
  sk: string;
  template_id: string;
  template_name: string;
  label: string;
  description?: string; 
}

const adminApi = new HttpAdminApi();

const TemplateFAQManager = ({ userState }: PropsType) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openTemplateDialog, setOpenTemplateDialog] = useState(false); // 템플릿 추가 모달 상태
  const [isEditTemplate, setIsEditTemplate] = useState(false);
  
  const [selectedFaq, setSelectedFaq] = useState<any>(null);
  const [newTemplateName, setNewTemplateName] = useState(''); // 새 템플릿 입력값
  const [newTemplateDesc, setNewTemplateDesc] = useState(''); // 새 템플릿 설명값
  
  // ✅ 템플릿 목록을 상태로 관리
  const [templates, setTemplates] = useState<TemplateType[]>([]);
  const [selecetedTemplate, setSelectedTemplate] = useState(""); // 템플릿 ID

  const [faqList, setFaqList] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // 템플릿 검색 필터링
  const filteredTemplates = templates.filter(t => 
    t.template_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  useEffect(() => {
    get_faq_template_list();
  }, []);

  useEffect(() => {
    if (selecetedTemplate) get_faq_list();
  }, [selecetedTemplate]);

  const get_faq_template_list = async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.post({ command: "get_faq_template_list" });
      if (res.code == "200") {
        const list = res.response.template_list || [];
        setTemplates(list);
        if (list.length > 0 && !selecetedTemplate) {
          setSelectedTemplate(list[0].template_id);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }

  const get_faq_list = async () => {
    setIsLoading(true);
    try {
      const res = await adminApi.post({ 
        command: "get_faq_list",
        template_id: selecetedTemplate
      });
      if (res.code === "200") setFaqList(res.response.faq_list || []);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ 새 템플릿 추가 로직
  const handleAddTemplate = async () => {
    if (!newTemplateName.trim()) return;
    
    setIsLoading(true);
    try {
      const res = await adminApi.post({
        command: "create_faq_template",
        name: newTemplateName,
        description: newTemplateDesc,
        user_id: userState.id
      });

      if (res.code === "200") {
        alert(`${newTemplateName} 템플릿이 생성되었습니다.`);
        await get_faq_template_list();
        setOpenTemplateDialog(false);
        setNewTemplateName("");
        setNewTemplateDesc("");
      }
    } catch (error) {
      alert("템플릿 생성 중 오류가 발생했습니다.")
    } finally {
      setIsLoading(false);
    }
  };

  // 템플릿 삭제 핸들러
  const handleDeleteTemplate = async (e: React.MouseEvent, template: any) => {
    // 이벤트 버블링 방지 (리스트 클릭 방지)
    e.stopPropagation();
    if (!window.confirm(`'${template.template_name}' 템플릿과 포함된 모든 FAQ가 삭제됩니다. 계속하시겠습니까?`)) return;

    setIsLoading(true);
    try {
      const res = await adminApi.post({ 
        command: "delete_faq_template", 
        template: template,
      });

      if (res.code === "200") {
        alert("삭제되었습니다.");
        await get_faq_template_list(); // 템플릿 목록 새로고침
      }
    } catch (error) {
      alert("삭제 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditTemplateOpen = () => {
    const currentTemplate = templates.find(t => t.template_id === selecetedTemplate);
    if (!currentTemplate) return;

    setNewTemplateName(currentTemplate.template_name);
    setNewTemplateDesc(currentTemplate.description || "");
    setIsEditTemplate(true);
    setOpenTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim()) return;
    
    setIsLoading(true);
    try {
      const command = isEditTemplate ? "update_faq_template" : "create_faq_template";
      const payload: any = {
        command: command,
        template_id: isEditTemplate ? selecetedTemplate : undefined,
        name: newTemplateName,
        description: newTemplateDesc,
        user_id: userState.id,
      };

      if (isEditTemplate) {
        payload.template_id = selecetedTemplate;
      }

      const res = await adminApi.post(payload);

      if (res.code === "200") {
        alert(`템플릿이 ${isEditTemplate ? '수정' : '생성'}되었습니다.`);
        await get_faq_template_list();
        setOpenTemplateDialog(false);
        setNewTemplateName("");
        setNewTemplateDesc("");
        setIsEditTemplate(false);
      }
    } catch (error) {
      alert("템플릿 처리 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
      resetTemplateFields();
    }
  };

  const handleOpenDialog = (faq: any = null) => {
    setSelectedFaq(faq || { question: '', answer: '' });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFaq(null);
  };

  // ✅ 입력 필드 초기화 공통 함수
  const resetTemplateFields = () => {
    setNewTemplateName("");
    setNewTemplateDesc("");
    setIsEditTemplate(false);
  };

  // ✅ 신규 생성 버튼 클릭 시 (기존 데이터 비우기)
  const handleCreateTemplateOpen = () => {
    resetTemplateFields(); // 필드 비우기
    setOpenTemplateDialog(true);
  };

  // ✅ 개별 FAQ 질문 삭제 핸들러
  const handleDeleteFAQ = async (faq: any) => {
    if (!window.confirm("이 질문을 삭제하시겠습니까?")) return;

    setIsLoading(true);
    try {
      // 현재 리스트에서 선택한 FAQ만 제외한 새 리스트 생성
      const filteredList = faqList.filter(item => item.faq_id !== faq.faq_id);

      // 서버로 보낼 때 is_common 등 UI용 필드 제거
      const reorderedList = filteredList.map((item, index) => {
        // is_common 필드 제외 (Destructuring)
        const { is_common, template_id, ...rest } = item;
        
        return {
          ...rest,
          sort_order: index // 현재 배열 내 위치를 새로운 순서로 확정
        };
      });

      const res = await adminApi.post({
        command: "create_template_faq",
        template_id: selecetedTemplate,
        faq_list: reorderedList,
      });

      if (res.code === "200") {
        alert("삭제되었습니다.");
        get_faq_list(); // 목록 새로고침
      }
    } catch (error) {
      alert("삭제 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedFaq?.question?.trim() || !selectedFaq?.answer?.trim()) return;
    setIsLoading(true);

    try {
      const isEdit = Boolean(selectedFaq.faq_id);

      // 1. 먼저 전체 리스트를 업데이트합니다.
      const tempUpdatedList = isEdit 
        ? faqList.map(item => (item.faq_id === selectedFaq.faq_id ? { ...item, ...selectedFaq } : item))
        : [...faqList, { ...selectedFaq }];

      // 2. 서버로 보낼 데이터에서 is_common 필드를 제거(Destructuring)하여 정제합니다.
      const cleanAndReorderedList = tempUpdatedList.map((item, index) => {
        // is_common 필드는 제외하고 나머지 속성만 추출
        const { is_common, template_id, ...rest } = item;
        
        return {
          ...rest,
          // 신규 항목인데 faq_id가 없다면 임시 ID 부여 (서버에서 생성 전까지 클라이언트 식별용)
          faq_id: item.faq_id, 
          sort_order: index // 0부터 차례대로 순서 재할당
        };
      });

      const res = await adminApi.post({
        command: "create_template_faq",
        template_id: selecetedTemplate,
        faq_list: cleanAndReorderedList,
      });
      
      if (res.code === "200") {
        handleCloseDialog();
        get_faq_list();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'sort_order', headerName: '순서', width: 60, align: 'center' },
    { field: 'question', headerName: '질문', flex: 1, minWidth: 200 },
    { field: 'answer', headerName: '답변', flex: 2, minWidth: 300 },
    {
      field: 'actions',
      headerName: '관리',
      width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row">
          <IconButton onClick={() => handleOpenDialog(params.row)} color="primary"><EditTwoToneIcon /></IconButton>
          <IconButton onClick={() => handleDeleteFAQ(params.row)} color="error"><DeleteIcon /></IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Container maxWidth="xl" sx={{ pt: 4 }}>
      <Stack direction="row" spacing={3} sx={{ height: '80vh' }}>
        
        {/* 1. 좌측 사이드바 */}
        <Paper sx={{ width: 320, flexShrink: 0, flexGrow: 0, display: 'flex', flexDirection: 'column', borderRadius: '12px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="800">📦 템플릿 목록</Typography>
              <Tooltip title="새 템플릿 추가">
                <IconButton size="small" color="primary" onClick={handleCreateTemplateOpen}>
                  <PostAddIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <TextField
              size="small"
              fullWidth
              placeholder="템플릿 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              }}
            />
          </Box>
          <Divider />
          <List sx={{ flex: 1, overflowY: 'auto', py: 0 }}>
            {filteredTemplates.map((temp) => (
              <ListItemButton 
                key={temp.template_id} 
                selected={selecetedTemplate === temp.template_id}
                onClick={() => setSelectedTemplate(temp.template_id)}
                sx={{ 
                  py: 1.5,
                  '&.Mui-selected': { bgcolor: '#e3f2fd', borderRight: '4px solid #1976d2' }
                }}
              >
                <ListItemText 
                  primary={temp.template_name} 
                  // secondary={temp.description}
                  primaryTypographyProps={{ fontWeight: selecetedTemplate === temp.template_id ? 700 : 500, fontSize: '0.9rem' }} 
                  // secondaryTypographyProps={{ fontSize: '0.75rem', noWrap: true }}
                />

                {/* 삭제 버튼 추가 */}
                <IconButton 
                  size="small" 
                  onClick={(e) => handleDeleteTemplate(e, temp)}
                  sx={{ 
                    visibility: 'hidden', // 평소엔 숨김
                    '.MuiListItemButton-root:hover &': { visibility: 'visible' }, // 호버 시 노출
                    '&:hover': { color: 'error.main' }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        </Paper>

        {/* 2. 우측 콘텐츠 */}
        <Box sx={{ flexGrow: 1, flexShrink: 1, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Card sx={{ width: '100%', flex: 1, borderRadius: '12px', display: 'flex', flexDirection: 'column', border: '1px solid #e0e0e0' }}>
            <Box 
              sx={{ 
                p: "20px 24px", 
                display: 'flex', 
                alignItems: 'flex-start', // 설명이 길어지면 상단 정렬
                justifyContent: 'space-between', 
                bgcolor: '#f8fafc', 
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              <Stack
                direction="row" 
                alignItems="flex-start" // 아이콘도 상단 정렬
                spacing={2.5} 
                sx={{ flex: 1, minWidth: 0 }}
              >
                <CategoryIcon color="primary" sx={{ fontSize: 32, mt: 0.5 }} /> 
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {/* 템플릿 이름 + 편집 버튼 */}
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography 
                      variant="h6" 
                      fontWeight="800" 
                      sx={{ lineHeight: 1.2 }}
                    >
                      {templates.find(t => t.template_id === selecetedTemplate)?.template_name || "템플릿을 선택하세요"}
                    </Typography>
                    
                    <Tooltip title="템플릿 정보 수정" placement="top">
                      <IconButton 
                        size="small" 
                        onClick={handleEditTemplateOpen}
                        sx={{ p: 0.5, color: 'text.secondary' }}
                      >
                        <EditTwoToneIcon sx={{ fontSize: '1.1rem' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {/* ✅ 템플릿 설명 (전체 노출하되 스크롤 추가) */}
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      whiteSpace: 'pre-wrap', // 줄바꿈 문자 및 자동 줄바꿈 허용
                      wordBreak: 'break-word', // 긴 단어도 영역 안에서 줄바꿈
                      lineHeight: 1.6,
                      display: 'block',
                      
                      // ✅ 고려된 추가 사항: 최대 높이 제한 및 스크롤 
                      maxHeight: '150px', // 적절한 최대 높이 설정 (조정 가능)
                      overflowY: 'auto', // 내용이 많으면 내부 세로 스크롤 생성
                      
                      // ✅ 스크롤바 스타일링 (깔끔하게)
                      '&::-webkit-scrollbar': {
                        width: '6px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#a8a8a8',
                      },
                    }}
                  >
                    {templates.find(t => t.template_id === selecetedTemplate)?.description || "등록된 설명이 없습니다."}
                  </Typography>
                </Box>
              </Stack>
              
              {/* 질문 추가 버튼 (위치 고정) */}
              <Button 
                variant="contained" 
                startIcon={<AddIcon />} 
                onClick={() => handleOpenDialog()}
                sx={{ 
                  ml: 4, 
                  flexShrink: 0, 
                  height: '42px', 
                  px: 3,
                  fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.25)' // 버튼 강조 효과
                }}
              >
                질문 추가
              </Button>
            </Box>
            <Divider />
            <Box sx={{ flex: 1 }}>
              <DataGrid
                rows={faqList}
                columns={columns}
                getRowId={(row) => row.faq_id || row.sk}
                loading={isLoading}
                disableRowSelectionOnClick
              />
            </Box>
          </Card>
        </Box>
      </Stack>

      {/* ✅ 템플릿 추가 모달 */}
      <Dialog 
        open={openTemplateDialog} 
        onClose={() => {
          setOpenTemplateDialog(false);
          setIsEditTemplate(false); // 닫을 때 상태 초기화
        }} 
        maxWidth="xs" 
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {isEditTemplate ? '📝 템플릿 수정' : '📦 새 템플릿 카테고리 생성'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {isEditTemplate ? '선택한 템플릿의 이름과 설명을 수정합니다.' : '방송별 FAQ의 기준이 되는 새로운 카테고리를 생성합니다.'}
            </Typography>
            <TextField
              autoFocus
              label="템플릿 이름"
              fullWidth
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
            />
            <TextField
              label="템플릿 설명"
              fullWidth
              multiline
              // rows={2}
              value={newTemplateDesc}
              onChange={(e) => setNewTemplateDesc(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setOpenTemplateDialog(false); setIsEditTemplate(false); }}>취소</Button>
          <Button onClick={handleSaveTemplate} variant="contained" disabled={!newTemplateName.trim()}>
            {isEditTemplate ? '수정하기' : '템플릿 생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAQ 등록/수정 모달 */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedFaq?.faq_id ? '📄 FAQ 수정' : '➕ 신규 FAQ 등록'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField label="질문" fullWidth value={selectedFaq?.question || ''} onChange={(e) => setSelectedFaq({...selectedFaq, question: e.target.value})} />
            <TextField label="답변" fullWidth multiline rows={5} value={selectedFaq?.answer || ''} onChange={(e) => setSelectedFaq({...selectedFaq, answer: e.target.value})} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDialog}>취소</Button>
          <Button onClick={handleConfirm} variant="contained">저장하기</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TemplateFAQManager;